import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import type { HillGrind, HillObstacle, HillVehicleKind } from "../hill-course";
import { paintOf, type Kit } from "./hill-kit";
import { inked } from "./toon";

// Bomb the Hill's obstacles (#194, #209; #217 moved them out of
// `hill-course.ts`): parked cars, vans and buses, cones, bins, barriers,
// rails, potholes and gravel, each built flat (y up from the road) with a
// nose to local +z, down the hill. `hill-course.ts` batches them a chunk
// at a time and drives the moving traffic with `vehicle`.

/** A car, van or bus `hw` x `hl` x `h`, nose to +z, wheels on y = 0; `lit`, its headlights on and throwing a beam ahead (#197). */
export function vehicle(k: Kit, kind: HillVehicleKind, size: { hw: number; hl: number; h: number }, tint: number, lit = false): THREE.Group {
  const g = new THREE.Group();
  const { hw, hl, h } = size;
  const body = paintOf(k, tint);
  const wheelR = kind === "bus" ? 0.5 : 0.34;
  const floor = wheelR * 0.9;
  if (kind === "car") {
    // A low body and a glassy cabin set back from the bonnet.
    const lower = inked(new RoundedBoxGeometry(hw * 2, h * 0.45, hl * 2, 3, 0.12), body, 0.02);
    lower.position.y = floor + h * 0.2;
    const cabin = inked(new RoundedBoxGeometry(hw * 1.75, h * 0.4, hl * 1.05, 3, 0.15), k.glass, 0.02);
    cabin.position.set(0, floor + h * 0.55, -hl * 0.12);
    const roof = inked(new RoundedBoxGeometry(hw * 1.7, 0.08, hl * 0.95, 2, 0.04), body, 0.015);
    roof.position.set(0, floor + h * 0.75 + 0.02, -hl * 0.12);
    g.add(lower, cabin, roof);
  } else {
    // A van and a bus are tall boxes with a band of windows; the bus longer, its windows all down the side.
    const shell = inked(new RoundedBoxGeometry(hw * 2, h - floor, hl * 2, 3, 0.15), body, 0.02);
    shell.position.y = floor + (h - floor) / 2;
    const band = new THREE.Mesh(new THREE.BoxGeometry(hw * 2 + 0.02, (h - floor) * 0.28, kind === "bus" ? hl * 1.8 : hl * 0.7), k.glass);
    band.position.set(0, floor + (h - floor) * 0.68, kind === "bus" ? 0 : hl * 0.45);
    const screen = new THREE.Mesh(new THREE.BoxGeometry(hw * 1.7, (h - floor) * 0.3, 0.04), k.glass);
    screen.position.set(0, floor + (h - floor) * 0.68, hl + 0.01);
    const back = new THREE.Mesh(new THREE.BoxGeometry(hw * 1.5, (h - floor) * 0.26, 0.04), k.glass);
    back.position.set(0, floor + (h - floor) * 0.7, -hl - 0.01);
    g.add(shell, band, screen, back);
  }
  const lights = new THREE.Mesh(new THREE.BoxGeometry(hw * 1.5, 0.1, 0.04), lit ? k.headlight : k.white);
  lights.position.set(0, floor + 0.25, hl + 0.02);
  if (lit) {
    // A pale wedge of light on the road ahead, so you see one coming from up the hill.
    const beam = new THREE.Mesh(new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2), k.beam);
    beam.scale.set(hw * 2.2, 1, 6);
    beam.position.set(0, 0.03, hl + 3);
    beam.renderOrder = 1;
    g.add(beam);
  }
  const bumper = new THREE.Mesh(new THREE.BoxGeometry(hw * 1.9, 0.12, 0.08), k.trim);
  bumper.position.set(0, floor + 0.05, -hl - 0.02);
  g.add(lights, bumper);
  const wheel = new THREE.CylinderGeometry(wheelR, wheelR, 0.24, 14).rotateZ(Math.PI / 2);
  const axles = kind === "bus" ? [-0.7, 0.72] : [-0.62, 0.62];
  for (const sx of [-1, 1]) {
    for (const az of axles) {
      const w = new THREE.Mesh(wheel, k.tyre);
      w.position.set(sx * (hw - 0.1), wheelR, az * hl);
      w.castShadow = true;
      g.add(w);
    }
  }
  return g;
}

function cone(k: Kit, o: HillObstacle): THREE.Object3D {
  const g = new THREE.Group();
  const base = new THREE.Mesh(new THREE.BoxGeometry(o.hw * 2.1, 0.04, o.hl * 2.1), k.cone);
  base.position.y = 0.02;
  const body = inked(new THREE.ConeGeometry(o.hw * 0.8, o.h - 0.04, 12), k.cone, 0.012);
  body.position.y = 0.04 + (o.h - 0.04) / 2;
  const stripe = new THREE.Mesh(new THREE.CylinderGeometry(o.hw * 0.42, o.hw * 0.52, 0.1, 12), k.white);
  stripe.position.y = o.h * 0.55;
  g.add(base, body, stripe);
  return g;
}

function bin(k: Kit, o: HillObstacle): THREE.Object3D {
  const g = new THREE.Group();
  const body = inked(new RoundedBoxGeometry(o.hw * 2, o.h - 0.08, o.hl * 2, 2, 0.05), k.bin, 0.015);
  body.position.y = (o.h - 0.08) / 2;
  const lid = inked(new RoundedBoxGeometry(o.hw * 2.1, 0.08, o.hl * 2.15, 2, 0.03), k.lid, 0.012);
  lid.position.y = o.h - 0.04;
  g.add(body, lid);
  return g;
}

/** A road barrier across the street: a striped plank on A-frame legs. */
function barrier(k: Kit, o: HillObstacle): THREE.Object3D {
  const g = new THREE.Group();
  const stripes = Math.max(2, Math.round(o.hw * 2 / 0.5));
  const w = (o.hw * 2) / stripes;
  for (let i = 0; i < stripes; i++) {
    const s = new THREE.Mesh(new THREE.BoxGeometry(w, 0.22, 0.08), i % 2 ? k.white : k.stripe);
    s.position.set(-o.hw + w * (i + 0.5), o.h - 0.11, 0);
    s.castShadow = true;
    g.add(s);
  }
  const leg = new THREE.BoxGeometry(0.06, o.h - 0.1, 0.06);
  for (const x of [-o.hw + 0.12, o.hw - 0.12]) {
    for (const lean of [-0.25, 0.25]) {
      const l = new THREE.Mesh(leg, k.metal);
      l.position.set(x, (o.h - 0.1) / 2, lean * 0.6);
      l.rotation.x = -lean;
      l.castShadow = true;
      g.add(l);
    }
  }
  return g;
}

/** A low rail along the road: a round bar on posts. */
function rail(k: Kit, o: HillObstacle): THREE.Object3D {
  const g = new THREE.Group();
  const bar = inked(new THREE.CylinderGeometry(0.04, 0.04, o.hl * 2, 10).rotateX(Math.PI / 2), k.metal, 0.01);
  bar.position.y = o.h;
  g.add(bar);
  const post = new THREE.CylinderGeometry(0.035, 0.035, o.h, 8).translate(0, o.h / 2, 0);
  const n = Math.max(2, Math.ceil((o.hl * 2) / 1.5) + 1);
  for (let i = 0; i < n; i++) {
    const p = new THREE.Mesh(post, k.metal);
    p.position.z = -o.hl + 0.05 + ((o.hl * 2 - 0.1) * i) / (n - 1);
    p.castShadow = true;
    g.add(p);
  }
  return g;
}

/**
 * A rail to grind down the hill (#237), its top end at local z = 0 and
 * running `len` on down: a thicker bar than the low rails you ollie,
 * painted red so it reads as one to grind (not a lane line, as a white one
 * did), on grey posts with a foot plate each.
 */
export function grindRail(k: Kit, g: HillGrind): THREE.Object3D {
  const root = new THREE.Group();
  const bar = inked(new THREE.CylinderGeometry(0.05, 0.05, g.len, 12).rotateX(Math.PI / 2), k.stripe, 0.012);
  bar.position.set(0, g.h, g.len / 2);
  root.add(bar);
  const post = new THREE.CylinderGeometry(0.04, 0.04, g.h, 8).translate(0, g.h / 2, 0);
  const foot = new THREE.BoxGeometry(0.2, 0.03, 0.2).translate(0, 0.015, 0);
  const n = Math.max(2, Math.ceil(g.len / 2) + 1);
  for (let i = 0; i < n; i++) {
    const z = 0.1 + ((g.len - 0.2) * i) / (n - 1);
    const p = new THREE.Mesh(post, k.pole);
    p.position.z = z;
    p.castShadow = true;
    const f = new THREE.Mesh(foot, k.pole);
    f.position.z = z;
    root.add(p, f);
  }
  return root;
}

/** A random stream from an obstacle's tint, so it's the same shape every build. */
function tintRand(tint: number): () => number {
  let a = Math.floor(tint * 1e6) + 1;
  return () => (a = (a * 16807) % 2147483647) / 2147483647;
}

/** A flat blob `hw` x `hl` on the road (y up), its edge broken in by up to `jag` of its size. */
function ragged(hw: number, hl: number, jag: number, rand: () => number): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  const n = 14;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    const r = 1 - jag * rand();
    if (i === 0) shape.moveTo(Math.cos(a) * hw * r, Math.sin(a) * hl * r);
    else shape.lineTo(Math.cos(a) * hw * r, Math.sin(a) * hl * r);
  }
  return new THREE.ShapeGeometry(shape).rotateX(-Math.PI / 2);
}

/**
 * A pothole (#209): a near-black hole with cracks running out of it, in a
 * pale broken rim, and chunks of broken road standing up round its lip, so
 * it shows from up the hill in every art style (only the hole itself is
 * hit, `o.hw` x `o.hl`). From a rider's eye a few metres up, anything flat on
 * the road is squashed to a sliver 40 m off, so the rim runs well out along
 * the road and the lip stands up off it.
 */
function pothole(k: Kit, o: HillObstacle): THREE.Object3D {
  const g = new THREE.Group();
  const rand = tintRand(o.tint);
  const rim = new THREE.Mesh(ragged(o.hw * 1.7, o.hl * 2.6, 0.18, rand), k.rim);
  rim.position.y = 0.012;
  rim.name = "rim";
  const hole = new THREE.Mesh(ragged(o.hw, o.hl, 0.12, rand), k.hole);
  hole.position.y = 0.016;
  hole.name = "hole";
  rim.receiveShadow = hole.receiveShadow = true;
  g.add(rim, hole);
  // Cracks out of the hole and across the rim onto the road.
  const crack = new THREE.BoxGeometry(0.035, 0.004, 1);
  for (let i = 0; i < 4; i++) {
    const a = ((i + rand() * 0.6) / 4) * Math.PI * 2;
    const len = 0.5 + rand() * 0.4;
    const c = new THREE.Mesh(crack, k.hole);
    const out = 0.8 + len / 2;
    c.scale.z = len;
    c.rotation.y = a;
    c.position.set(Math.sin(a) * o.hw * out, 0.017, Math.cos(a) * o.hl * out);
    c.name = "crack";
    g.add(c);
  }
  // The broken lip: chunks of road heaved up round the edge, catching the light.
  const chunk = new THREE.DodecahedronGeometry(0.12, 0);
  for (let i = 0; i < 10; i++) {
    const a = ((i + rand() * 0.5) / 10) * Math.PI * 2;
    const r = 1.05 + rand() * 0.2;
    const c = new THREE.Mesh(chunk, k.rim);
    c.scale.set(0.8 + rand() * 0.6, 0.8 + rand() * 0.5, 0.8 + rand() * 0.6);
    c.rotation.set(rand() * 3, rand() * 3, rand() * 3);
    c.position.set(Math.cos(a) * o.hw * r, 0.02, Math.sin(a) * o.hl * r);
    c.castShadow = true;
    c.name = "lip";
    g.add(c);
  }
  return g;
}

/** A gravel patch: pale grit strewn with dark stones, about one a square metre (#209), a few spilt past its edge. */
function gravel(k: Kit, o: HillObstacle): THREE.Object3D {
  const g = new THREE.Group();
  const patch = new THREE.Mesh(new THREE.BoxGeometry(o.hw * 2, 0.02, o.hl * 2), k.gravel);
  patch.position.y = 0.01;
  patch.receiveShadow = true;
  patch.name = "patch";
  g.add(patch);
  const stone = new THREE.DodecahedronGeometry(0.07, 0);
  const rand = tintRand(o.tint);
  const n = Math.min(60, Math.max(14, Math.round(o.hw * o.hl * 4 * 1.2)));
  for (let i = 0; i < n; i++) {
    const s = new THREE.Mesh(stone, k.pebble);
    s.position.set((rand() * 2 - 1) * (o.hw + 0.1), 0.035, (rand() * 2 - 1) * (o.hl + 0.15));
    s.rotation.set(rand() * 3, rand() * 3, 0);
    s.name = "stone";
    g.add(s);
  }
  return g;
}

/** A pothole's or gravel patch's meshes, as a chunk draws them before batching (#209). */
export function hazardMeshes(k: Kit, o: HillObstacle): THREE.Object3D {
  return o.kind === "gravel" ? gravel(k, o) : pothole(k, o);
}

export function obstacle(k: Kit, o: HillObstacle): THREE.Object3D {
  switch (o.kind) {
    case "car":
    case "van":
    case "bus":
      return vehicle(k, o.kind, o, o.tint);
    case "cone":
      return cone(k, o);
    case "bin":
      return bin(k, o);
    case "barrier":
      return barrier(k, o);
    case "rail":
      return rail(k, o);
    case "pothole":
      return pothole(k, o);
    case "gravel":
      return gravel(k, o);
  }
}
